import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";

const MATERIALS = [
    { value: "RM", label: "Raw Material", hint: "Requires item_name, quantity, batch_number, po_number, invoice_number" },
    {
        value: "WIP",
        label: "WIP",
        hint:
            "Requires item_name, quantity. Optional work_order_code links to an existing Work Order only if that WO has a Build Order for the WIP item (creates a new Daily Plan). Blank creates dummy Sales Order / Work Order / Daily Plan using GEV-FG / GEV-FG-WO / GEV-FG-DP codes. If the item is_fg, the FG product is taken from BOM and FG stock is credited as WIP qty ÷ req_quan_of_item.",
    },
];

const STATUS_OPTIONS = [
    { value: "", label: "All statuses" },
    { value: "imported", label: "Imported" },
    { value: "validated", label: "Validated" },
    { value: "failed", label: "Failed" },
];

const emptyResult = () => ({
    success: false,
    dry_run: true,
    run_id: "",
    material_type: "",
    valid_row_count: 0,
    imported_count: 0,
    errors: [],
    warnings: [],
    stats: null,
});

const statusBadgeClass = (status) => {
    switch (status) {
        case "imported":
            return "badge bg-success";
        case "validated":
            return "badge bg-info";
        case "failed":
            return "badge bg-danger";
        default:
            return "badge bg-secondary";
    }
};

const formatDateTime = (value) => {
    if (!value) return "—";
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
};

const StockOnboarding = () => {
    const privileges = secureLocalStorage.getItem("privileges") || [];
    const userID = secureLocalStorage.getItem("userID");
    const { apiClient } = useGlobalAPIQueryContext();

    const [activeTab, setActiveTab] = useState("upload");

    const [materialType, setMaterialType] = useState("RM");
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastDryRunOk, setLastDryRunOk] = useState(false);
    const [dryRunFingerprint, setDryRunFingerprint] = useState("");
    const [result, setResult] = useState(emptyResult());
    const fileInputRef = useRef(null);

    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyFilterMaterial, setHistoryFilterMaterial] = useState("");
    const [historyFilterStatus, setHistoryFilterStatus] = useState("");
    const [historySearch, setHistorySearch] = useState("");
    const [historySearchApplied, setHistorySearchApplied] = useState("");
    const [historyPage, setHistoryPage] = useState(1);
    const [historyEntries, setHistoryEntries] = useState(20);
    const [historyCount, setHistoryCount] = useState(0);
    const [historyNumPages, setHistoryNumPages] = useState(0);

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [fileDownloading, setFileDownloading] = useState(false);

    const canImport = privileges.includes("stock_onboarding_import");
    const materialMeta = useMemo(
        () => MATERIALS.find((item) => item.value === materialType),
        [materialType]
    );

    const currentFingerprint = selectedFile
        ? `${materialType}:${selectedFile.name}:${selectedFile.size}:${selectedFile.lastModified}`
        : "";

    const importEnabled =
        canImport &&
        !!selectedFile &&
        lastDryRunOk &&
        dryRunFingerprint === currentFingerprint &&
        !loading;

    const resetOutcome = () => {
        setLastDryRunOk(false);
        setDryRunFingerprint("");
        setResult(emptyResult());
    };

    const handleMaterialChange = (value) => {
        setMaterialType(value);
        resetOutcome();
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0] || null;
        setSelectedFile(file);
        resetOutcome();
    };

    const fetchHistory = useCallback(async () => {
        try {
            setHistoryLoading(true);
            const params = {
                entries: historyEntries,
                page_no: historyPage,
            };
            if (historyFilterMaterial) params.material_type = historyFilterMaterial;
            if (historyFilterStatus) params.status = historyFilterStatus;
            if (historySearchApplied) params.q = historySearchApplied;

            const response = await apiClient.get("/api/stock-onboarding/runs/", { params });
            const payload = response.data || {};
            const rows = payload.results?.data || payload.data || [];
            setHistoryRows(Array.isArray(rows) ? rows : []);
            setHistoryCount(payload.count || 0);
            setHistoryNumPages(payload.num_pages || 0);
        } catch (error) {
            Swal.fire({
                title: "Failed to load history",
                text:
                    error.response?.data?.error ||
                    error.response?.data?.detail ||
                    error.message,
                icon: "error",
            });
            setHistoryRows([]);
            setHistoryCount(0);
            setHistoryNumPages(0);
        } finally {
            setHistoryLoading(false);
        }
    }, [
        apiClient,
        historyEntries,
        historyFilterMaterial,
        historyFilterStatus,
        historyPage,
        historySearchApplied,
    ]);

    useEffect(() => {
        if (activeTab === "history" && canImport) {
            fetchHistory();
        }
    }, [activeTab, canImport, fetchHistory]);

    const downloadTemplate = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get("/api/stock-onboarding/template/", {
                params: { material_type: materialType },
                responseType: "blob",
            });
            const blob = new Blob([response.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = `stock_onboarding_${materialType.toLowerCase()}_template.csv`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            Swal.fire({
                title: "Template download failed",
                text:
                    error.response?.data?.error ||
                    error.response?.data?.detail ||
                    error.message,
                icon: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const runUpload = async ({ dryRun }) => {
        if (!selectedFile) {
            Swal.fire({
                title: "Choose a CSV file",
                text: "Select a stock onboarding CSV before continuing.",
                icon: "warning",
            });
            return;
        }

        if (!dryRun) {
            const confirm = await Swal.fire({
                title: "Commit stock import?",
                text: `This will add ${materialType} opening stock from "${selectedFile.name}".`,
                showDenyButton: true,
                confirmButtonText: "Import",
                denyButtonText: "Cancel",
                icon: "question",
            });
            if (!confirm.isConfirmed) {
                return;
            }
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append("material_type", materialType);
            formData.append("created_by", userID);
            formData.append("updated_by", userID);

            const response = await apiClient.post("/api/stock-onboarding/import/", formData, {
                params: { dry_run: dryRun ? "true" : "false" },
            });
            const payload = response.data || {};
            setResult(payload);

            if (dryRun) {
                setLastDryRunOk(true);
                setDryRunFingerprint(currentFingerprint);
                Swal.fire({
                    title: "Validation passed",
                    text: `${payload.valid_row_count || 0} rows are ready to import.`,
                    icon: "success",
                });
            } else {
                setLastDryRunOk(false);
                setDryRunFingerprint("");
                Swal.fire({
                    title: "Stock imported",
                    text: `${payload.imported_count || 0} rows imported successfully.`,
                    icon: "success",
                });
                if (activeTab === "history") {
                    fetchHistory();
                }
            }
        } catch (error) {
            const payload = error.response?.data || {};
            setResult({
                ...emptyResult(),
                ...payload,
                success: false,
                dry_run: dryRun,
                material_type: materialType,
            });
            setLastDryRunOk(false);
            setDryRunFingerprint("");

            const firstErrors = payload.errors
                ?.slice(0, 8)
                ?.map((row) => `Row ${row.row}: ${row.error}`)
                ?.join("\n");

            Swal.fire({
                title: dryRun ? "Validation failed" : "Import failed",
                text:
                    firstErrors ||
                    payload.detail ||
                    payload.error ||
                    error.message,
                icon: "error",
            });
        } finally {
            setLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const openDetail = async (runId) => {
        try {
            setDetailOpen(true);
            setDetailLoading(true);
            setDetail(null);
            const response = await apiClient.get(`/api/stock-onboarding/runs/${runId}/`);
            setDetail(response.data || null);
        } catch (error) {
            setDetailOpen(false);
            Swal.fire({
                title: "Failed to load run detail",
                text:
                    error.response?.data?.detail ||
                    error.response?.data?.error ||
                    error.message,
                icon: "error",
            });
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setDetailOpen(false);
        setDetail(null);
    };

    const downloadRunFile = async (run) => {
        if (!run?.id) return;
        try {
            setFileDownloading(true);
            const response = await apiClient.get(`/api/stock-onboarding/runs/${run.id}/file/`, {
                responseType: "blob",
            });
            const blob = new Blob([response.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = run.original_filename || `${run.run_id || "stock_import"}.csv`;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            Swal.fire({
                title: "Download failed",
                text:
                    error.response?.data?.detail ||
                    error.response?.data?.error ||
                    error.message,
                icon: "error",
            });
        } finally {
            setFileDownloading(false);
        }
    };

    const applyHistorySearch = () => {
        setHistoryPage(1);
        setHistorySearchApplied(historySearch.trim());
    };

    if (!canImport) {
        return (
            <div className="nk-content">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <div className="card card-bordered">
                                <div className="card-inner">
                                    <h5 className="card-title">Stock Onboarding</h5>
                                    <p className="text-soft mb-0">
                                        You do not have the <code>stock_onboarding_import</code> privilege.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const stats = result.stats || {};
    const errors = result.errors || [];
    const warnings = result.warnings || [];
    const quantityByItem = stats.quantity_by_item || [];
    const detailStats = detail?.stats || {};
    const detailQuantityByItem = detailStats.quantity_by_item || [];
    const detailErrors = detail?.errors || [];
    const detailWarnings = detail?.warnings || [];

    return (
        <div className="nk-content">
            <div className="container-fluid">
                <div className="nk-content-inner">
                    <div className="nk-content-body">
                        <div className="nk-block-head nk-block-head-sm">
                            <div className="nk-block-between">
                                <div className="nk-block-head-content">
                                    <h3 className="nk-block-title page-title">Stock Onboarding</h3>
                                    <div className="nk-block-des text-soft">
                                        <p>
                                            Upload opening stock CSV for Raw Material or WIP.
                                            Validate with a dry run, then commit the import. Review past imports
                                            on the History tab.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="nk-block">
                            <div className="card card-bordered">
                                <div className="card-inner border-bottom">
                                    <ul className="nav nav-tabs mt-n3">
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link ${activeTab === "upload" ? "active" : ""}`}
                                                onClick={() => setActiveTab("upload")}
                                            >
                                                Upload
                                            </button>
                                        </li>
                                        <li className="nav-item">
                                            <button
                                                type="button"
                                                className={`nav-link ${activeTab === "history" ? "active" : ""}`}
                                                onClick={() => setActiveTab("history")}
                                            >
                                                History
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                {activeTab === "upload" && (
                                    <div className="card-inner">
                                        <div className="row g-3">
                                            <div className="col-md-4">
                                                <label className="form-label">Material type</label>
                                                <select
                                                    className="form-select"
                                                    value={materialType}
                                                    onChange={(event) => handleMaterialChange(event.target.value)}
                                                    disabled={loading}
                                                >
                                                    {MATERIALS.map((item) => (
                                                        <option key={item.value} value={item.value}>
                                                            {item.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="form-note mt-1">{materialMeta?.hint}</div>
                                            </div>
                                            <div className="col-md-5">
                                                <label className="form-label">CSV file</label>
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept=".csv,text/csv"
                                                    className="form-control"
                                                    onChange={handleFileChange}
                                                    disabled={loading}
                                                />
                                                <div className="form-note mt-1">
                                                    {selectedFile
                                                        ? `Selected: ${selectedFile.name}`
                                                        : "Choose a CSV matching the template headers."}
                                                </div>
                                            </div>
                                            <div className="col-md-3 d-flex align-items-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary w-100"
                                                    onClick={downloadTemplate}
                                                    disabled={loading}
                                                >
                                                    Download template
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mt-4 d-flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-warning"
                                                disabled={!selectedFile || loading}
                                                onClick={() => runUpload({ dryRun: true })}
                                            >
                                                {loading ? "Working…" : "Validate (Dry run)"}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={!importEnabled}
                                                onClick={() => runUpload({ dryRun: false })}
                                            >
                                                Import
                                            </button>
                                        </div>

                                        {!lastDryRunOk && selectedFile && (
                                            <div className="alert alert-light mt-3 mb-0">
                                                Run <strong>Validate (Dry run)</strong> successfully before Import is enabled.
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === "history" && (
                                    <div className="card-inner">
                                        <div className="row g-3 align-items-end mb-3">
                                            <div className="col-md-3">
                                                <label className="form-label">Material type</label>
                                                <select
                                                    className="form-select"
                                                    value={historyFilterMaterial}
                                                    onChange={(event) => {
                                                        setHistoryPage(1);
                                                        setHistoryFilterMaterial(event.target.value);
                                                    }}
                                                >
                                                    <option value="">All materials</option>
                                                    {MATERIALS.map((item) => (
                                                        <option key={item.value} value={item.value}>
                                                            {item.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label">Status</label>
                                                <select
                                                    className="form-select"
                                                    value={historyFilterStatus}
                                                    onChange={(event) => {
                                                        setHistoryPage(1);
                                                        setHistoryFilterStatus(event.target.value);
                                                    }}
                                                >
                                                    {STATUS_OPTIONS.map((item) => (
                                                        <option key={item.value || "all"} value={item.value}>
                                                            {item.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Search</label>
                                                <div className="form-control-wrap">
                                                    <div className="input-group">
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="run_id or filename"
                                                            value={historySearch}
                                                            onChange={(event) => setHistorySearch(event.target.value)}
                                                            onKeyDown={(event) => {
                                                                if (event.key === "Enter") applyHistorySearch();
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-primary"
                                                            onClick={applyHistorySearch}
                                                        >
                                                            Search
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary w-100"
                                                    onClick={fetchHistory}
                                                    disabled={historyLoading}
                                                >
                                                    {historyLoading ? "Loading…" : "Refresh"}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-soft">
                                                {historyCount} run{historyCount === 1 ? "" : "s"}
                                            </span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: "auto" }}
                                                value={historyEntries}
                                                onChange={(event) => {
                                                    setHistoryPage(1);
                                                    setHistoryEntries(Number(event.target.value));
                                                }}
                                            >
                                                <option value={10}>10 entries</option>
                                                <option value={20}>20 entries</option>
                                                <option value={50}>50 entries</option>
                                            </select>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>Date / time</th>
                                                        <th>Run ID</th>
                                                        <th>Material</th>
                                                        <th>Dry run</th>
                                                        <th>Status</th>
                                                        <th>Filename</th>
                                                        <th>Rows</th>
                                                        <th>Items</th>
                                                        <th>Total qty</th>
                                                        <th>Actor</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historyLoading && (
                                                        <tr>
                                                            <td colSpan={11} className="text-center text-soft">
                                                                Loading…
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {!historyLoading && historyRows.length === 0 && (
                                                        <tr>
                                                            <td colSpan={11} className="text-center text-soft">
                                                                No import runs found.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {!historyLoading &&
                                                        historyRows.map((row) => (
                                                            <tr key={row.id}>
                                                                <td>{formatDateTime(row.created_at)}</td>
                                                                <td>
                                                                    <code className="text-soft">{row.run_id}</code>
                                                                </td>
                                                                <td>{row.material_type}</td>
                                                                <td>{row.dry_run ? "Yes" : "No"}</td>
                                                                <td>
                                                                    <span className={statusBadgeClass(row.status)}>
                                                                        {row.status}
                                                                    </span>
                                                                </td>
                                                                <td className="text-break">
                                                                    {row.original_filename || "—"}
                                                                </td>
                                                                <td>{row.row_count ?? 0}</td>
                                                                <td>{row.items_touched ?? 0}</td>
                                                                <td>{row.total_quantity ?? 0}</td>
                                                                <td>{row.created_by ?? "—"}</td>
                                                                <td>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => openDetail(row.id)}
                                                                    >
                                                                        View
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {historyNumPages > 1 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-light"
                                                    disabled={historyPage <= 1 || historyLoading}
                                                    onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}
                                                >
                                                    Previous
                                                </button>
                                                <span className="text-soft">
                                                    Page {historyPage} of {historyNumPages}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-light"
                                                    disabled={
                                                        historyPage >= historyNumPages || historyLoading
                                                    }
                                                    onClick={() =>
                                                        setHistoryPage((page) =>
                                                            Math.min(historyNumPages, page + 1)
                                                        )
                                                    }
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {activeTab === "upload" && errors.length > 0 && (
                            <div className="nk-block">
                                <div className="card card-bordered border-danger">
                                    <div className="card-inner">
                                        <h6 className="title">Errors ({errors.length})</h6>
                                        <div className="table-responsive">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Row</th>
                                                        <th>Column</th>
                                                        <th>Error</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {errors.map((row, index) => (
                                                        <tr key={`${row.row}-${index}`}>
                                                            <td>{row.row}</td>
                                                            <td>{row.column || "—"}</td>
                                                            <td>{row.error}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "upload" && warnings.length > 0 && (
                            <div className="nk-block">
                                <div className="card card-bordered border-warning">
                                    <div className="card-inner">
                                        <h6 className="title">Warnings ({warnings.length})</h6>
                                        <ul className="list-sm">
                                            {warnings.map((row, index) => (
                                                <li key={index}>
                                                    {row.row ? `Row ${row.row}: ` : ""}
                                                    {row.warning || row}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "upload" && result.success && stats && (
                            <div className="nk-block">
                                <div className="card card-bordered border-success">
                                    <div className="card-inner">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h6 className="title mb-0">
                                                {result.dry_run ? "Dry-run plan" : "Import summary"}
                                            </h6>
                                            <span className="badge bg-outline-primary">
                                                run_id: {result.run_id || "—"}
                                            </span>
                                        </div>

                                        <div className="row g-3">
                                            <div className="col-sm-6 col-lg-3">
                                                <div className="profile-ud">
                                                    <span className="profile-ud-label">Items touched</span>
                                                    <span className="profile-ud-value">{stats.items_touched || 0}</span>
                                                </div>
                                            </div>
                                            <div className="col-sm-6 col-lg-3">
                                                <div className="profile-ud">
                                                    <span className="profile-ud-label">Total quantity</span>
                                                    <span className="profile-ud-value">{stats.total_quantity || 0}</span>
                                                </div>
                                            </div>
                                            <div className="col-sm-6 col-lg-3">
                                                <div className="profile-ud">
                                                    <span className="profile-ud-label">
                                                        {result.dry_run ? "Valid rows" : "Imported rows"}
                                                    </span>
                                                    <span className="profile-ud-value">
                                                        {result.dry_run
                                                            ? result.valid_row_count || 0
                                                            : result.imported_count || 0}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-sm-6 col-lg-3">
                                                <div className="profile-ud">
                                                    <span className="profile-ud-label">Saved file</span>
                                                    <span className="profile-ud-value text-break">
                                                        {stats.storage_path || "—"}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {materialType === "RM" && (
                                            <div className="row g-3 mt-1">
                                                <div className="col-md-4">
                                                    Batches created / reused:{" "}
                                                    <strong>
                                                        {stats.batches_created || 0} / {stats.batches_reused || 0}
                                                    </strong>
                                                </div>
                                                <div className="col-md-4">
                                                    Invoices created / reused:{" "}
                                                    <strong>
                                                        {stats.invoices_created || 0} / {stats.invoices_reused || 0}
                                                    </strong>
                                                </div>
                                                <div className="col-md-4">
                                                    POs created / reused:{" "}
                                                    <strong>
                                                        {stats.pos_created || 0} / {stats.pos_reused || 0}
                                                    </strong>
                                                </div>
                                            </div>
                                        )}

                                        {materialType === "WIP" && (
                                            <div className="row g-3 mt-1">
                                                <div className="col-md-3">
                                                    Existing WOs linked:{" "}
                                                    <strong>{stats.existing_work_orders_linked || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    Dummy sales orders:{" "}
                                                    <strong>{stats.dummy_sales_orders || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    Dummy work orders:{" "}
                                                    <strong>{stats.dummy_work_orders || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    Daily plans: <strong>{stats.daily_plans_created || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    FG stock posts:{" "}
                                                    <strong>{stats.fg_stock_posted || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    FG qty credited:{" "}
                                                    <strong>{stats.fg_quantity_posted || 0}</strong>
                                                </div>
                                            </div>
                                        )}

                                        {quantityByItem.length > 0 && (
                                            <div className="table-responsive mt-4">
                                                <table className="table table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Item</th>
                                                            <th>Quantity</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {quantityByItem.map((row) => (
                                                            <tr key={row.item_name}>
                                                                <td>{row.item_name}</td>
                                                                <td>{row.quantity}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {detailOpen && (
                <div
                    className="modal fade show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
                >
                    <div className="modal-dialog modal-xl" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Import run {detail?.run_id || ""}
                                </h5>
                                <a
                                    href="#close"
                                    className="close"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        closeDetail();
                                    }}
                                >
                                    <em className="icon ni ni-cross"></em>
                                </a>
                            </div>
                            <div className="modal-body">
                                {detailLoading && <p className="text-soft mb-0">Loading…</p>}
                                {!detailLoading && detail && (
                                    <>
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-3">
                                                <span className="text-soft d-block">Status</span>
                                                <span className={statusBadgeClass(detail.status)}>
                                                    {detail.status}
                                                </span>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-soft d-block">Material</span>
                                                <strong>{detail.material_type}</strong>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-soft d-block">Dry run</span>
                                                <strong>{detail.dry_run ? "Yes" : "No"}</strong>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-soft d-block">Created</span>
                                                <strong>{formatDateTime(detail.created_at)}</strong>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-soft d-block">Actor</span>
                                                <strong>{detail.created_by ?? "—"}</strong>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-soft d-block">Rows / success</span>
                                                <strong>
                                                    {detail.row_count ?? 0} / {detail.success_count ?? 0}
                                                </strong>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-soft d-block">Items / qty</span>
                                                <strong>
                                                    {detail.items_touched ?? 0} / {detail.total_quantity ?? 0}
                                                </strong>
                                            </div>
                                            <div className="col-md-3">
                                                <span className="text-soft d-block">Filename</span>
                                                <strong className="text-break">
                                                    {detail.original_filename || "—"}
                                                </strong>
                                            </div>
                                        </div>

                                        {detail.material_type === "RM" && (
                                            <div className="row g-2 mb-3">
                                                <div className="col-md-4">
                                                    Batches created / reused:{" "}
                                                    <strong>
                                                        {detailStats.batches_created || 0} /{" "}
                                                        {detailStats.batches_reused || 0}
                                                    </strong>
                                                </div>
                                                <div className="col-md-4">
                                                    Invoices created / reused:{" "}
                                                    <strong>
                                                        {detailStats.invoices_created || 0} /{" "}
                                                        {detailStats.invoices_reused || 0}
                                                    </strong>
                                                </div>
                                                <div className="col-md-4">
                                                    POs created / reused:{" "}
                                                    <strong>
                                                        {detailStats.pos_created || 0} /{" "}
                                                        {detailStats.pos_reused || 0}
                                                    </strong>
                                                </div>
                                            </div>
                                        )}

                                        {detail.material_type === "WIP" && (
                                            <div className="row g-2 mb-3">
                                                <div className="col-md-3">
                                                    Existing WOs linked:{" "}
                                                    <strong>
                                                        {detailStats.existing_work_orders_linked || 0}
                                                    </strong>
                                                </div>
                                                <div className="col-md-3">
                                                    Dummy sales orders:{" "}
                                                    <strong>{detailStats.dummy_sales_orders || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    Dummy work orders:{" "}
                                                    <strong>{detailStats.dummy_work_orders || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    Daily plans:{" "}
                                                    <strong>{detailStats.daily_plans_created || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    FG stock posts:{" "}
                                                    <strong>{detailStats.fg_stock_posted || 0}</strong>
                                                </div>
                                                <div className="col-md-3">
                                                    FG qty credited:{" "}
                                                    <strong>{detailStats.fg_quantity_posted || 0}</strong>
                                                </div>
                                            </div>
                                        )}

                                        {detailQuantityByItem.length > 0 && (
                                            <div className="mb-3">
                                                <h6 className="title">Quantity by item</h6>
                                                <div className="table-responsive">
                                                    <table className="table table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Item</th>
                                                                <th>Quantity</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {detailQuantityByItem.map((row) => (
                                                                <tr key={row.item_name}>
                                                                    <td>{row.item_name}</td>
                                                                    <td>{row.quantity}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {detailErrors.length > 0 && (
                                            <div className="mb-3">
                                                <h6 className="title text-danger">
                                                    Errors ({detailErrors.length})
                                                </h6>
                                                <div className="table-responsive">
                                                    <table className="table table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Row</th>
                                                                <th>Column</th>
                                                                <th>Error</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {detailErrors.map((row, index) => (
                                                                <tr key={`${row.row}-${index}`}>
                                                                    <td>{row.row}</td>
                                                                    <td>{row.column || "—"}</td>
                                                                    <td>{row.error}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {detailWarnings.length > 0 && (
                                            <div className="mb-3">
                                                <h6 className="title text-warning">
                                                    Warnings ({detailWarnings.length})
                                                </h6>
                                                <ul className="list-sm mb-0">
                                                    {detailWarnings.map((row, index) => (
                                                        <li key={index}>
                                                            {row.row ? `Row ${row.row}: ` : ""}
                                                            {row.warning || row}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {detail.storage_path && (
                                            <p className="text-soft mb-0">
                                                Storage path: <code>{detail.storage_path}</code>
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="modal-footer bg-light">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    disabled={!detail?.has_file || fileDownloading || detailLoading}
                                    onClick={() => downloadRunFile(detail)}
                                >
                                    {fileDownloading ? "Downloading…" : "Download CSV"}
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={closeDetail}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockOnboarding;
